/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FEATURE LIST - Master Feature Definitions
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Single source of truth for all subscription tiers, features, and limits.
 * 
 * RESPONSIBILITIES:
 * - Define all available features and their keys
 * - Map features to subscription tiers
 * - Define usage limits per tier
 * - Provide tier metadata and descriptions
 * 
 * SYNC TO DATABASE:
 * - Run: npm run sync-features
 * - Updates subscription_tiers table with latest configurations
 * - Always sync after modifying TIER_CONFIGS
 * 
 * USAGE IN CODE:
 * - Import feature keys: import { FEATURES } from './features/featureList.js'
 * - Check requirements: getRequiredTierName(FEATURES.CSV_EXPORT)
 * - Get tier config: getTierConfigByLevel(tierLevel)
 * 
 * VERSION: 1.0.0
 * LAST UPDATED: 2024
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ══════════════════════════════════════════════════════════════════════════════
// FEATURE KEYS - All Available Features
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Feature key constants
 * Use these in permission checks: await requireFeature(FEATURES.CSV_EXPORT)
 */
export const FEATURES = {
  // ────────────────────────────────────────────────────────────────────────────
  // CORE DATABASE ACCESS (Professional+)
  // ────────────────────────────────────────────────────────────────────────────
  CLOUD_DATABASE: 'cloud_database_access',
  
  // ────────────────────────────────────────────────────────────────────────────
  // PAYMENT FEATURES
  // ────────────────────────────────────────────────────────────────────────────
  PAYMENT_TRACKING: 'payment_tracking',           // Professional+
  PAYMENT_ANALYTICS: 'payment_analytics',         // Exclusive only
  REFUND_MANAGEMENT: 'refund_management',         // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // INVOICE FEATURES
  // ────────────────────────────────────────────────────────────────────────────
  INVOICE_EDITING: 'invoice_editing',             // Exclusive only
  INVOICE_RESTORE: 'invoice_restore',             // Exclusive only
  CUSTOM_INVOICE_NUMBERING: 'custom_invoice_numbering', // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // SEARCH & FILTERING
  // ────────────────────────────────────────────────────────────────────────────
  SEARCH: 'search',                               // Professional+
  FILTER_BY_PAYMENT_STATUS: 'filter_by_payment_status', // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // EXPORT FEATURES
  // ────────────────────────────────────────────────────────────────────────────
  JSON_EXPORT: 'json_export',                     // Professional+
  CSV_EXPORT: 'csv_export',                       // Exclusive only
  AUDIT_EXPORT: 'audit_export',                   // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // ANALYTICS & REPORTS
  // ────────────────────────────────────────────────────────────────────────────
  DASHBOARD_ANALYTICS: 'dashboard_analytics',     // Exclusive only
  SALES_REPORTS: 'sales_reports',                 // Exclusive only
  PRODUCT_ANALYTICS: 'product_analytics',         // Exclusive only
  CUSTOMER_ANALYTICS: 'customer_analytics',       // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // CUSTOMIZATION
  // ────────────────────────────────────────────────────────────────────────────
  CUSTOM_FIELDS: 'custom_fields',                 // Exclusive only (2 fields)
  DISPLAY_OPTIONS: 'display_options',             // Professional+
  ADVANCED_DISPLAY_OPTIONS: 'advanced_display_options', // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // IMAGE MANAGEMENT
  // ────────────────────────────────────────────────────────────────────────────
  IMAGE_STORAGE: 'image_storage',                 // Professional+
  IMAGE_ANALYTICS: 'image_analytics',             // Exclusive only
  
  // ────────────────────────────────────────────────────────────────────────────
  // COMPLIANCE & DATA MANAGEMENT
  // ────────────────────────────────────────────────────────────────────────────
  RIGHT_TO_BE_FORGOTTEN: 'right_to_be_forgotten', // Professional+ (GDPR)
  DATA_EXPORT_GDPR: 'data_export_gdpr',           // Professional+ (GDPR)
  
  // ────────────────────────────────────────────────────────────────────────────
  // INTEGRITY & DIAGNOSTICS
  // ────────────────────────────────────────────────────────────────────────────
  INTEGRITY_CHECKS: 'integrity_checks',           // Exclusive only
  DIAGNOSTICS: 'diagnostics'                      // Exclusive only
};

// ══════════════════════════════════════════════════════════════════════════════
// LIMIT KEYS - Usage Limit Types
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Limit key constants
 * Used for quota enforcement
 */
export const LIMITS = {
  INVOICES_PER_MONTH: 'invoices_per_month',       // Resets monthly (calendar)
  PRODUCTS_LIFETIME: 'products_lifetime',         // Lifetime limit
  CUSTOMERS_LIFETIME: 'customers_lifetime'        // Lifetime limit
};

// ══════════════════════════════════════════════════════════════════════════════
// FEATURE DESCRIPTIONS - Human-Readable Information
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Feature descriptions for UI display
 * Used in upgrade prompts, feature lists, and documentation
 */
export const FEATURE_DESCRIPTIONS = {
  [FEATURES.CLOUD_DATABASE]: {
    name: 'Cloud Database',
    description: 'Permanently save all data to secure cloud database',
    icon: '☁️'
  },
  
  [FEATURES.PAYMENT_TRACKING]: {
    name: 'Payment Tracking',
    description: 'Track payments, partial payments, and outstanding dues',
    icon: '💰'
  },
  
  [FEATURES.PAYMENT_ANALYTICS]: {
    name: 'Payment Analytics',
    description: 'Detailed payment summaries and revenue analytics',
    icon: '📊'
  },
  
  [FEATURES.REFUND_MANAGEMENT]: {
    name: 'Refund Management',
    description: 'Record refunds and transfer payments between invoices',
    icon: '↩️'
  },
  
  [FEATURES.INVOICE_EDITING]: {
    name: 'Invoice Editing',
    description: 'Edit existing invoices with full audit trail',
    icon: '✏️'
  },
  
  [FEATURES.INVOICE_RESTORE]: {
    name: 'Invoice Recovery',
    description: 'Restore individual soft-deleted invoices',
    icon: '♻️'
  },
  
  [FEATURES.CUSTOM_INVOICE_NUMBERING]: {
    name: 'Custom Invoice Numbering',
    description: 'Customize invoice number format and prefix',
    icon: '🔢'
  },
  
  [FEATURES.SEARCH]: {
    name: 'Search',
    description: 'Search invoices, products, and customers',
    icon: '🔍'
  },
  
  [FEATURES.FILTER_BY_PAYMENT_STATUS]: {
    name: 'Payment Status Filters',
    description: 'Filter invoices by paid/unpaid/partial status',
    icon: '🎯'
  },
  
  [FEATURES.JSON_EXPORT]: {
    name: 'JSON Export',
    description: 'Export data in JSON format',
    icon: '📦'
  },
  
  [FEATURES.CSV_EXPORT]: {
    name: 'CSV Export',
    description: 'Export data in CSV format for Excel/Sheets',
    icon: '📊'
  },
  
  [FEATURES.AUDIT_EXPORT]: {
    name: 'Audit Export',
    description: 'Accountant-friendly export without snapshots',
    icon: '📋'
  },
  
  [FEATURES.DASHBOARD_ANALYTICS]: {
    name: 'Dashboard Analytics',
    description: 'Real-time dashboard statistics and insights',
    icon: '📈'
  },
  
  [FEATURES.SALES_REPORTS]: {
    name: 'Sales Reports',
    description: 'Comprehensive sales reporting and trends',
    icon: '📊'
  },
  
  [FEATURES.PRODUCT_ANALYTICS]: {
    name: 'Product Analytics',
    description: 'Product performance and inventory insights',
    icon: '📦'
  },
  
  [FEATURES.CUSTOMER_ANALYTICS]: {
    name: 'Customer Analytics',
    description: 'Customer segmentation and behavior analysis',
    icon: '👥'
  },
  
  [FEATURES.CUSTOM_FIELDS]: {
    name: 'Custom Fields',
    description: 'Add up to 2 custom fields to products',
    icon: '🏷️'
  },
  
  [FEATURES.DISPLAY_OPTIONS]: {
    name: 'Display Options',
    description: 'Customize invoice display settings',
    icon: '🎨'
  },
  
  [FEATURES.ADVANCED_DISPLAY_OPTIONS]: {
    name: 'Advanced Display Options',
    description: 'Full control over invoice appearance',
    icon: '🎨'
  },
  
  [FEATURES.IMAGE_STORAGE]: {
    name: 'Image Storage',
    description: 'Persistently save logos, signatures, and watermarks',
    icon: '🖼️'
  },
  
  [FEATURES.IMAGE_ANALYTICS]: {
    name: 'Image Analytics',
    description: 'Storage statistics and image management insights',
    icon: '📊'
  },
  
  [FEATURES.RIGHT_TO_BE_FORGOTTEN]: {
    name: 'Right to be Forgotten',
    description: 'GDPR-compliant data deletion with recovery period',
    icon: '🗑️'
  },
  
  [FEATURES.DATA_EXPORT_GDPR]: {
    name: 'Data Export (GDPR)',
    description: 'Export all your data for portability',
    icon: '📤'
  },
  
  [FEATURES.INTEGRITY_CHECKS]: {
    name: 'Integrity Checks',
    description: 'Detect duplicates and data inconsistencies',
    icon: '🔍'
  },
  
  [FEATURES.DIAGNOSTICS]: {
    name: 'Diagnostics',
    description: 'Advanced diagnostics and gap detection',
    icon: '🔧'
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// TIER CONFIGURATIONS - Complete Tier Definitions
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Complete subscription tier configurations
 * Synced to subscription_tiers table via npm run sync-features
 * 
 * STRUCTURE:
 * - tier_id: Database identifier (primary key)
 * - tier_name: Display name
 * - tier_level: Numeric level for comparisons (0=basic, 1=professional, 2=exclusive)
 * - monthly_price: Price in cents (800 = $8.00)
 * - description: Marketing description
 * - features: Object of feature flags { feature_key: true/false }
 * - limits: Object of usage limits { limit_key: number|null } (null = unlimited)
 */
export const TIER_CONFIGS = {
  // ═══════════════════════════════════════════════════════════════════════════
  // BASIC TIER - FREE (Local Storage Only)
  // ═══════════════════════════════════════════════════════════════════════════
  basic: {
    tier_id: 'basic',
    tier_name: 'Basic',
    tier_level: 0,
    monthly_price: 0,
    description: 'Free tier for trial users - all data stored locally in browser',
    
    // ─────────────────────────────────────────────────────────────────────────
    // ALL FEATURES DISABLED - Local state only
    // ─────────────────────────────────────────────────────────────────────────
    features: {
      [FEATURES.CLOUD_DATABASE]: false,
      [FEATURES.PAYMENT_TRACKING]: false,
      [FEATURES.PAYMENT_ANALYTICS]: false,
      [FEATURES.REFUND_MANAGEMENT]: false,
      [FEATURES.INVOICE_EDITING]: false,
      [FEATURES.INVOICE_RESTORE]: false,
      [FEATURES.CUSTOM_INVOICE_NUMBERING]: false,
      [FEATURES.SEARCH]: false,
      [FEATURES.FILTER_BY_PAYMENT_STATUS]: false,
      [FEATURES.JSON_EXPORT]: false,
      [FEATURES.CSV_EXPORT]: false,
      [FEATURES.AUDIT_EXPORT]: false,
      [FEATURES.DASHBOARD_ANALYTICS]: false,
      [FEATURES.SALES_REPORTS]: false,
      [FEATURES.PRODUCT_ANALYTICS]: false,
      [FEATURES.CUSTOMER_ANALYTICS]: false,
      [FEATURES.CUSTOM_FIELDS]: false,
      [FEATURES.DISPLAY_OPTIONS]: false,
      [FEATURES.ADVANCED_DISPLAY_OPTIONS]: false,
      [FEATURES.IMAGE_STORAGE]: false,
      [FEATURES.IMAGE_ANALYTICS]: false,
      [FEATURES.RIGHT_TO_BE_FORGOTTEN]: false,
      [FEATURES.DATA_EXPORT_GDPR]: false,
      [FEATURES.INTEGRITY_CHECKS]: false,
      [FEATURES.DIAGNOSTICS]: false
    },
    
    // ─────────────────────────────────────────────────────────────────────────
    // LOCAL STORAGE LIMITS (Enforced by frontend)
    // ─────────────────────────────────────────────────────────────────────────
    limits: {
      [LIMITS.INVOICES_PER_MONTH]: 10,   // 10 invoices per calendar month
      [LIMITS.PRODUCTS_LIFETIME]: 5,     // 5 products total
      [LIMITS.CUSTOMERS_LIFETIME]: 5     // 5 customers total
    }
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PROFESSIONAL TIER - $8/month (Small Businesses)
  // ═══════════════════════════════════════════════════════════════════════════
  professional: {
    tier_id: 'professional',
    tier_name: 'Professional',
    tier_level: 1,
    monthly_price: 800, // $8.00 in cents
    description: 'For small businesses - cloud storage, payment tracking, and essential features',
    
    features: {
      // ───────────────────────────────────────────────────────────────────────
      // ENABLED FEATURES (Professional Tier)
      // ───────────────────────────────────────────────────────────────────────
      
      // Core Database Access
      [FEATURES.CLOUD_DATABASE]: true,              // ✅ All CRUD operations
      
      // Payments
      [FEATURES.PAYMENT_TRACKING]: true,            // ✅ Basic payment tracking
      
      // Search
      [FEATURES.SEARCH]: true,                      // ✅ Search all entities
      
      // Export
      [FEATURES.JSON_EXPORT]: true,                 // ✅ JSON export only
      
      // Customization
      [FEATURES.DISPLAY_OPTIONS]: true,             // ✅ Basic display options
      
      // Images
      [FEATURES.IMAGE_STORAGE]: true,               // ✅ Persistent image storage
      
      // Compliance
      [FEATURES.RIGHT_TO_BE_FORGOTTEN]: true,       // ✅ GDPR deletion
      [FEATURES.DATA_EXPORT_GDPR]: true,            // ✅ GDPR data export
      
      // ───────────────────────────────────────────────────────────────────────
      // DISABLED FEATURES (Exclusive Only)
      // ───────────────────────────────────────────────────────────────────────
      [FEATURES.PAYMENT_ANALYTICS]: false,          // ❌ Exclusive
      [FEATURES.REFUND_MANAGEMENT]: false,          // ❌ Exclusive
      [FEATURES.INVOICE_EDITING]: false,            // ❌ Exclusive
      [FEATURES.INVOICE_RESTORE]: false,            // ❌ Exclusive
      [FEATURES.CUSTOM_INVOICE_NUMBERING]: false,   // ❌ Exclusive
      [FEATURES.FILTER_BY_PAYMENT_STATUS]: false,   // ❌ Exclusive
      [FEATURES.CSV_EXPORT]: false,                 // ❌ Exclusive
      [FEATURES.AUDIT_EXPORT]: false,               // ❌ Exclusive
      [FEATURES.DASHBOARD_ANALYTICS]: false,        // ❌ Exclusive
      [FEATURES.SALES_REPORTS]: false,              // ❌ Exclusive
      [FEATURES.PRODUCT_ANALYTICS]: false,          // ❌ Exclusive
      [FEATURES.CUSTOMER_ANALYTICS]: false,         // ❌ Exclusive
      [FEATURES.CUSTOM_FIELDS]: false,              // ❌ Exclusive
      [FEATURES.ADVANCED_DISPLAY_OPTIONS]: false,   // ❌ Exclusive
      [FEATURES.IMAGE_ANALYTICS]: false,            // ❌ Exclusive
      [FEATURES.INTEGRITY_CHECKS]: false,           // ❌ Exclusive
      [FEATURES.DIAGNOSTICS]: false                 // ❌ Exclusive
    },
    
    // ─────────────────────────────────────────────────────────────────────────
    // DATABASE LIMITS
    // ─────────────────────────────────────────────────────────────────────────
    limits: {
      [LIMITS.INVOICES_PER_MONTH]: 1000,  // 1,000 invoices per calendar month
      [LIMITS.PRODUCTS_LIFETIME]: 500,    // 500 products total
      [LIMITS.CUSTOMERS_LIFETIME]: 200    // 200 customers total
    }
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // EXCLUSIVE TIER - $12/month (Growing Businesses)
  // ═══════════════════════════════════════════════════════════════════════════
  exclusive: {
    tier_id: 'exclusive',
    tier_name: 'Exclusive',
    tier_level: 2,
    monthly_price: 1200, // $12.00 in cents
    description: 'For growing businesses - unlimited invoices and all advanced features',
    
    features: {
      // ───────────────────────────────────────────────────────────────────────
      // ALL FEATURES ENABLED
      // ───────────────────────────────────────────────────────────────────────
      
      // Core Database Access
      [FEATURES.CLOUD_DATABASE]: true,              // ✅ All CRUD operations
      
      // Payments
      [FEATURES.PAYMENT_TRACKING]: true,            // ✅ Payment tracking
      [FEATURES.PAYMENT_ANALYTICS]: true,           // ✅ Payment summaries
      [FEATURES.REFUND_MANAGEMENT]: true,           // ✅ Refunds & transfers
      
      // Invoices
      [FEATURES.INVOICE_EDITING]: true,             // ✅ Edit invoices
      [FEATURES.INVOICE_RESTORE]: true,             // ✅ Restore deleted
      [FEATURES.CUSTOM_INVOICE_NUMBERING]: true,    // ✅ Custom numbering
      
      // Search & Filtering
      [FEATURES.SEARCH]: true,                      // ✅ Search all entities
      [FEATURES.FILTER_BY_PAYMENT_STATUS]: true,    // ✅ Filter by payment
      
      // Export
      [FEATURES.JSON_EXPORT]: true,                 // ✅ JSON export
      [FEATURES.CSV_EXPORT]: true,                  // ✅ CSV export
      [FEATURES.AUDIT_EXPORT]: true,                // ✅ Audit export
      
      // Analytics
      [FEATURES.DASHBOARD_ANALYTICS]: true,         // ✅ Dashboard stats
      [FEATURES.SALES_REPORTS]: true,               // ✅ Sales reports
      [FEATURES.PRODUCT_ANALYTICS]: true,           // ✅ Product insights
      [FEATURES.CUSTOMER_ANALYTICS]: true,          // ✅ Customer insights
      
      // Customization
      [FEATURES.CUSTOM_FIELDS]: true,               // ✅ 2 custom fields
      [FEATURES.DISPLAY_OPTIONS]: true,             // ✅ Display options
      [FEATURES.ADVANCED_DISPLAY_OPTIONS]: true,    // ✅ Advanced options
      
      // Images
      [FEATURES.IMAGE_STORAGE]: true,               // ✅ Image storage
      [FEATURES.IMAGE_ANALYTICS]: true,             // ✅ Storage stats
      
      // Compliance
      [FEATURES.RIGHT_TO_BE_FORGOTTEN]: true,       // ✅ GDPR deletion
      [FEATURES.DATA_EXPORT_GDPR]: true,            // ✅ GDPR export
      
      // Integrity & Diagnostics
      [FEATURES.INTEGRITY_CHECKS]: true,            // ✅ Data integrity
      [FEATURES.DIAGNOSTICS]: true                  // ✅ Diagnostics
    },
    
    // ─────────────────────────────────────────────────────────────────────────
    // DATABASE LIMITS
    // ─────────────────────────────────────────────────────────────────────────
    limits: {
      [LIMITS.INVOICES_PER_MONTH]: null,    // ∞ Unlimited invoices
      [LIMITS.PRODUCTS_LIFETIME]: 2000,     // 2,000 products (can request more)
      [LIMITS.CUSTOMERS_LIFETIME]: 1000     // 1,000 customers (can request more)
    }
  }
};

// ══════════════════════════════════════════════════════════════════════════════
// TIER METADATA - Quick Reference & Mapping
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Tier level to tier_id mapping
 * Used for quick tier lookups by level
 */
export const TIER_LEVELS = {
  0: 'basic',
  1: 'professional',
  2: 'exclusive'
};

/**
 * Tier display names
 * Used for UI display
 */
export const TIER_NAMES = {
  basic: 'Basic',
  professional: 'Professional',
  exclusive: 'Exclusive'
};

/**
 * All tier IDs as array
 * Used for iteration and validation
 */
export const ALL_TIER_IDS = Object.keys(TIER_CONFIGS);

/**
 * Tier count
 */
export const TIER_COUNT = ALL_TIER_IDS.length;

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS - Utility Functions for Feature & Tier Lookups
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get minimum tier level required for a feature
 * 
 * @param {string} featureKey - Feature key from FEATURES constant
 * @returns {number} Minimum tier level (0, 1, or 2)
 * 
 * @example
 * const level = getRequiredTierLevel(FEATURES.CSV_EXPORT);
 * // Returns: 2 (Exclusive tier)
 */
export function getRequiredTierLevel(featureKey) {
  // Iterate tiers from lowest to highest
  for (let level = 0; level < TIER_COUNT; level++) {
    const tierId = TIER_LEVELS[level];
    const config = TIER_CONFIGS[tierId];
    
    if (config.features[featureKey] === true) {
      return level;
    }
  }
  
  // Feature not found - default to highest tier (safest)
  return 2;
}

/**
 * Get minimum tier name required for a feature
 * 
 * @param {string} featureKey - Feature key from FEATURES constant
 * @returns {string} Tier name ('Basic', 'Professional', or 'Exclusive')
 * 
 * @example
 * const tierName = getRequiredTierName(FEATURES.PAYMENT_TRACKING);
 * // Returns: "Professional"
 */
export function getRequiredTierName(featureKey) {
  const level = getRequiredTierLevel(featureKey);
  const tierId = TIER_LEVELS[level];
  return TIER_NAMES[tierId];
}

/**
 * Get all enabled features for a specific tier level
 * 
 * @param {number} tierLevel - Tier level (0, 1, or 2)
 * @returns {string[]} Array of enabled feature keys
 * 
 * @example
 * const features = getFeaturesForTierLevel(1);
 * // Returns: ['cloud_database_access', 'payment_tracking', 'search', ...]
 */
export function getFeaturesForTierLevel(tierLevel) {
  const tierId = TIER_LEVELS[tierLevel];
  const config = TIER_CONFIGS[tierId];
  
  if (!config) {
    return [];
  }
  
  return Object.entries(config.features)
    .filter(([_, enabled]) => enabled === true)
    .map(([featureKey, _]) => featureKey);
}

/**
 * Get tier configuration by tier level
 * 
 * @param {number} tierLevel - Tier level (0, 1, or 2)
 * @returns {object} Complete tier configuration object
 * 
 * @example
 * const config = getTierConfigByLevel(1);
 * // Returns: { tier_id: 'professional', tier_name: 'Professional', ... }
 */
export function getTierConfigByLevel(tierLevel) {
  const tierId = TIER_LEVELS[tierLevel];
  return TIER_CONFIGS[tierId] || TIER_CONFIGS.basic; // Fallback to basic
}

/**
 * Get tier configuration by tier ID
 * 
 * @param {string} tierId - Tier ID ('basic', 'professional', or 'exclusive')
 * @returns {object} Complete tier configuration object
 * 
 * @example
 * const config = getTierConfigById('professional');
 * // Returns: { tier_id: 'professional', tier_level: 1, ... }
 */
export function getTierConfigById(tierId) {
  return TIER_CONFIGS[tierId] || TIER_CONFIGS.basic; // Fallback to basic
}

/**
 * Check if a tier level has a specific feature
 * 
 * @param {number} tierLevel - Tier level (0, 1, or 2)
 * @param {string} featureKey - Feature key from FEATURES constant
 * @returns {boolean} True if tier has the feature enabled
 * 
 * @example
 * const hasFeature = tierHasFeature(1, FEATURES.CSV_EXPORT);
 * // Returns: false (Professional doesn't have CSV export)
 */
export function tierHasFeature(tierLevel, featureKey) {
  const config = getTierConfigByLevel(tierLevel);
  return config.features[featureKey] === true;
}

/**
 * Get limit value for a specific tier and limit type
 * 
 * @param {number} tierLevel - Tier level (0, 1, or 2)
 * @param {string} limitKey - Limit key from LIMITS constant
 * @returns {number|null} Limit value (null means unlimited)
 * 
 * @example
 * const limit = getLimitForTier(1, LIMITS.INVOICES_PER_MONTH);
 * // Returns: 1000
 * 
 * const unlimited = getLimitForTier(2, LIMITS.INVOICES_PER_MONTH);
 * // Returns: null (unlimited)
 */
export function getLimitForTier(tierLevel, limitKey) {
  const config = getTierConfigByLevel(tierLevel);
  return config.limits[limitKey];
}

/**
 * Check if a limit is unlimited for a tier
 * 
 * @param {number} tierLevel - Tier level (0, 1, or 2)
 * @param {string} limitKey - Limit key from LIMITS constant
 * @returns {boolean} True if limit is unlimited (null)
 * 
 * @example
 * const isUnlimited = isLimitUnlimited(2, LIMITS.INVOICES_PER_MONTH);
 * // Returns: true
 */
export function isLimitUnlimited(tierLevel, limitKey) {
  const limit = getLimitForTier(tierLevel, limitKey);
  return limit === null;
}

/**
 * Get human-readable feature description
 * 
 * @param {string} featureKey - Feature key from FEATURES constant
 * @returns {object|null} Feature description object or null if not found
 * 
 * @example
 * const desc = getFeatureDescription(FEATURES.CSV_EXPORT);
 * // Returns: { name: 'CSV Export', description: '...', icon: '📊' }
 */
export function getFeatureDescription(featureKey) {
  return FEATURE_DESCRIPTIONS[featureKey] || null;
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY & USAGE DOCUMENTATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EXPORTS REFERENCE
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * CONSTANTS:
 * ──────────
 * • FEATURES - All feature keys (use in permission checks)
 * • LIMITS - All limit keys (use in quota checks)
 * • FEATURE_DESCRIPTIONS - Human-readable feature info
 * • TIER_CONFIGS - Complete tier definitions (sync to database)
 * • TIER_LEVELS - Level→ID mapping { 0: 'basic', 1: 'professional', ... }
 * • TIER_NAMES - Display names { basic: 'Basic', ... }
 * • ALL_TIER_IDS - Array of tier IDs ['basic', 'professional', 'exclusive']
 * • TIER_COUNT - Total number of tiers (3)
 * 
 * HELPER FUNCTIONS:
 * ─────────────────
 * • getRequiredTierLevel(featureKey) → number
 * • getRequiredTierName(featureKey) → string
 * • getFeaturesForTierLevel(tierLevel) → string[]
 * • getTierConfigByLevel(tierLevel) → object
 * • getTierConfigById(tierId) → object
 * • tierHasFeature(tierLevel, featureKey) → boolean
 * • getLimitForTier(tierLevel, limitKey) → number|null
 * • isLimitUnlimited(tierLevel, limitKey) → boolean
 * • getFeatureDescription(featureKey) → object|null
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLES
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * // Import features
 * import { FEATURES, LIMITS, getRequiredTierName } from './features/featureList.js';
 * 
 * // Check required tier for upgrade prompt
 * const requiredTier = getRequiredTierName(FEATURES.CSV_EXPORT);
 * console.log(`CSV export requires ${requiredTier} tier`);
 * // Output: "CSV export requires Exclusive tier"
 * 
 * // Check if user's tier has a feature
 * import { tierHasFeature } from './features/featureList.js';
 * const userTierLevel = 1; // Professional
 * const canExportCSV = tierHasFeature(userTierLevel, FEATURES.CSV_EXPORT);
 * // Returns: false
 * 
 * // Get tier limits
 * import { getLimitForTier, LIMITS } from './features/featureList.js';
 * const invoiceLimit = getLimitForTier(1, LIMITS.INVOICES_PER_MONTH);
 * console.log(`Professional tier: ${invoiceLimit} invoices/month`);
 * // Output: "Professional tier: 1000 invoices/month"
 * 
 * // Get all features for a tier
 * import { getFeaturesForTierLevel } from './features/featureList.js';
 * const professionalFeatures = getFeaturesForTierLevel(1);
 * console.log('Professional features:', professionalFeatures);
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */